import React, {useEffect,useMemo,useState} from 'react';
import axios from 'axios';
import {API_BASE_URL} from '../config';
import './ExchangeBookmaker.css';
const {scenarios,eligibility,validTick} = require('./bookmaker/raceBookRisk.cjs');
const L = require('./bookmaker/orderLifecycle.cjs');

type Runner={selection_id:string;name:string;status:string;best_back:number|null;best_lay:number|null};
type Offer={selection_id:string;enabled:boolean;min_odds:number;max_odds:number;target_odds:number;max_accepted_stake:number;max_runner_liability:number;accepted_stake:number};
type Market={market_id:string;status:string;in_play:boolean;runners:Runner[];start_time?:string;name?:string};
type Meeting={meeting_name:string;races:{race_number:number;race_name:string;betfair_start_time?:string}[]};
const M=(n:number)=>new Intl.NumberFormat('en-AU',{style:'currency',currency:'AUD',maximumFractionDigits:2}).format(n);
function daySydney(offset=0){
 const parts=new Intl.DateTimeFormat('en-CA',{timeZone:'Australia/Sydney',year:'numeric',month:'2-digit',day:'2-digit'}).format(new Date());
 const d=new Date(parts+'T00:00:00Z');d.setUTCDate(d.getUTCDate()+offset);return d.toISOString().slice(0,10);
}
const num=(v:any)=>Number.isFinite(Number(v))?Number(v):null;
const parseMarket=(raw:any):Market|null=>{
 const m=raw?.betfairMarket;
 if(!m?.market_id||!Array.isArray(m.runners)||m.runners.length<2)return null;
 return {market_id:String(m.market_id),status:String(m.status||'UNKNOWN'),
  in_play:m.in_play===true,runners:m.runners.map((r:any)=>({
   selection_id:String(r.selection_id),name:String(r.runner_name||r.name||r.selection_id),
   status:String(r.status||'ACTIVE'),best_back:num(r.best_back),
   best_lay:num(r.best_lay)
  }))};
};
const initOffer=(r:Runner):Offer=>({
 selection_id:r.selection_id,enabled:false,min_odds:2,max_odds:10,target_odds:3,
 max_accepted_stake:0,max_runner_liability:0,accepted_stake:0
});
export default function ExchangeBookmaker(){
 const [date,setDate]=useState(daySydney(1));
 const [meetings,setMeetings]=useState<Meeting[]>([]);
 const [meeting,setMeeting]=useState('');
 const [race,setRace]=useState(0);
 const [market,setMarket]=useState<Market|null>(null);
 const [offers,setOffers]=useState<Offer[]>([]);
 const [orders,setOrders]=useState<any[]>([]);
 const [bank,setBank]=useState(5000);
 const [raceLoss,setRaceLoss]=useState(250);
 const [accountLoss,setAccountLoss]=useState(400);
 const [otherMarketsRisk,setOtherMarketsRisk]=useState<number|null>(0);
 const [message,setMessage]=useState('');
 const [error,setError]=useState('');
 const [loading,setLoading]=useState(false);
 useEffect(()=>{
  let active=true;
  setMeeting('');setRace(0);setMarket(null);setOffers([]);setOrders([]);
  axios.get(API_BASE_URL+'/api/meetings/date/'+date,{params:{betfair_only:'1'},timeout:18000})
   .then(r=>{if(active)setMeetings((r.data||[]).filter((x:any)=>Array.isArray(x.races)&&x.races.length));})
   .catch(()=>{if(active){setMeetings([]);setError('Could not load Betfair meetings');}});
  return()=>{active=false};
 },[date]);
 useEffect(()=>{
  if(!meeting||!race)return;
  let active=true;setLoading(true);setMarket(null);setOrders([]);setOffers([]);setMessage('');setError('');
  axios.get(API_BASE_URL+'/api/races/'+encodeURIComponent(meeting)+'/'+race,{params:{date},timeout:20000})
   .then(r=>{
    if(!active)return;
    const m=parseMarket(r.data);
    setMarket(m);setOffers(m?m.runners.map(initOffer):[]);
    if(!m)setError('No mapped WIN market is available for this race');
   }).catch(()=>{if(active)setError('Race market details unavailable');})
   .finally(()=>{if(active)setLoading(false);});
  return()=>{active=false};
 },[meeting,race,date]);
 const activeMeet=meetings.find(x=>x.meeting_name===meeting);
 const update=(id:string,field:keyof Offer,value:any)=>setOffers(old=>old.map(o=>o.selection_id===id?{...o,[field]:value}:o));
 // This model is an explicit, hypothetical sheet: the actual market status is
 // shown separately. Never pass it into the live execution system.
 const book=useMemo(()=>market?({
  market_id:market.market_id,sport:'HORSE',market_type:'WIN',number_of_winners:1,
  status:'OPEN',in_play:false,delayed:false,
  runners:market.runners.map(r=>({selection_id:r.selection_id,status:r.status})),
  matched:orders.flatMap(x=>L.exposure(x).matched),
  open:orders.flatMap(x=>L.exposure(x).open)
 }):null,[market,orders]);
 const policy=useMemo(()=>({runners:offers.map(o=>({
  selection_id:o.selection_id,enabled:o.enabled,min_odds:o.min_odds,max_odds:o.max_odds,
  max_accepted_stake:o.max_accepted_stake,max_runner_liability:o.max_runner_liability
 }))}),[offers]);
 const snapshot=useMemo(()=>{if(!book)return null;
  try{return scenarios(book,{commission:0.1})}catch{return null}
 },[book]);
 const approve=(o:Offer)=>{
  if(!book)return;
  const amount=Math.floor(o.accepted_stake*100)/100;
  let decision:any;
  try{
   decision=eligibility(book,policy,{selection_id:o.selection_id,odds:o.target_odds,stake:amount},
    {maxRaceLoss:raceLoss,maxAccountLoss:accountLoss,existingAccountRisk:otherMarketsRisk,
     availableFunds:bank,commission:0.1});
  }catch(e:any){setMessage('No test offer: '+e.message);return;}
  if(!decision.allowed){setMessage('Rejected: '+decision.reasons.join(', '));return;}
  try{
   const id='SIM/'+book.market_id+'/'+o.selection_id+'/'+Date.now()+'/'+orders.length;
   const t=new Date().toISOString();
   let order=L.newDraft({intent_id:id,market_id:book.market_id,
    selection_id:o.selection_id,side:'LAY',sport:'HORSE',market_type:'WIN',
    number_of_winners:1,limit_odds:o.target_odds,stake:amount,
    policy_version:'SIMULATION_ONLY',quote_observed_at:t});
   order=L.apply(order,{kind:'RESERVE',event_id:id+'/reserve',
    intent_id:id,expected_version:0,observed_at:t,risk_checked:true,
    account_proof_id:'SIMULATED_FUNDS_NOT_VERIFIED',
    policy_snapshot_id:'SIMULATED_NOT_APPROVED',
    race_book_version:'SIMULATED_NOT_RECONCILED'});
   order=L.apply(order,{kind:'EXCHANGE_ACK',event_id:id+'/ack',
    intent_id:id,expected_version:1,observed_at:t,
    exchange_status:'SUCCESS',exchange_bet_id:'SIMULATED/'+id});
   setOrders(old=>[...old,order]);
   setMessage('Simulated exchange acknowledgement created. No actual order was sent.');
  }catch(e:any){setMessage('Simulated offer rejected: '+String(e?.message||e));}
 };
 const advanceOrder=(order:any,kind:string,extra:any={})=>{
  try{
   const event={kind,event_id:'SIM/'+order.intent_id+'/'+kind+'/'+order.ledger_version,
    intent_id:order.intent_id,expected_version:order.ledger_version,
    observed_at:new Date().toISOString(),...extra};
   const next=L.apply(order,event);
   setOrders(old=>old.map(x=>x.intent_id===order.intent_id?next:x));
   setMessage('SIMULATION: '+kind+' recorded. No actual Betfair action.');
  }catch(e:any){setMessage('Simulation event rejected: '+String(e?.message||e));}
 };
 const simulatedFill=(order:any,all:boolean)=>{
  const remaining=Math.max(0,(order.stake_cents-order.matched_cents)/100);
  const stake=all?remaining:Math.max(0.01,Math.floor(remaining*50)/100);
  advanceOrder(order,'MATCH',{exchange_confirmed:true,
   fill_id:'SIM/'+order.intent_id+'/'+order.ledger_version,
   matched_odds:order.limit_odds,matched_stake:stake,
   exchange_bet_id:order.exchange_bet_id});
 };
 return <div className="eb-page">
  <header className="eb-heading"><div><p className="eb-eyebrow">BETBUILDER / EXCHANGE BOOKMAKER</p>
   <h1>Selective race book</h1><p>Horse racing prototype · WIN LAY only · one selected meeting · zero risk by default</p></div>
   <span className="eb-safe">SIMULATION ONLY — NO EXCHANGE ORDERS</span></header>
  <div className="eb-controls">
   <label>Meeting date <input type="date" value={date} onChange={e=>setDate(e.target.value)}/></label>
   <label>One selected meeting <select value={meeting} onChange={e=>{setMeeting(e.target.value);setRace(0);setMarket(null);setOrders([])}}>
    <option value="">Choose meeting (liquidity ranking not certified)</option>
    {meetings.map(m=><option key={m.meeting_name} value={m.meeting_name}>{m.meeting_name}</option>)}
   </select></label>
   <label>Race <select value={race} onChange={e=>setRace(Number(e.target.value))}>
    <option value={0}>Choose race</option>{(activeMeet?.races||[]).map(r=><option key={r.race_number} value={r.race_number}>R{r.race_number} {r.race_name}</option>)}
   </select></label>
  </div>
  <div className="eb-risk">
   <label>Illustrative bank ($)<input type="number" min={0} value={bank} onChange={e=>setBank(Math.max(0,Number(e.target.value)||0))}/></label>
   <label>Max race loss ($)<input type="number" min={0} value={raceLoss} onChange={e=>setRaceLoss(Math.max(0,Number(e.target.value)||0))}/></label>
   <label>Max account loss ($)<input type="number" min={0} value={accountLoss} onChange={e=>setAccountLoss(Math.max(0,Number(e.target.value)||0))}/></label>
   <label>Other markets risk ($)<input type="number" min={0} value={otherMarketsRisk===null?'':otherMarketsRisk} onChange={e=>setOtherMarketsRisk(e.target.value===''?null:Math.max(0,Number(e.target.value)||0))}/></label>
  </div>
  <p className="eb-caption">These are <strong>hypothetical inputs</strong> only. They are not connected to your live bank or account risk settings. The quote admission control is a preview and does not authorise an order.</p>
  {loading&&<p>Loading race market…</p>}{error&&<p role="alert" className="eb-error">{error}</p>}
  {market&&<><div className="eb-market-title"><div><strong>Market {market.market_id}</strong>
    <small>Exchange status: {market.status}{market.in_play?' · IN PLAY':''} · {market.runners.length} mapped runners</small></div>
    <button onClick={()=>{setOffers(old=>old.map(x=>({...x,enabled:false,accepted_stake:0})));setOrders([]);setMessage('All offers withdrawn from the simulated book only.')}}>Reset / No offers</button></div>
  <div className="eb-scroll"><table className="eb-table"><thead><tr>
   <th>Runner</th><th>Quote?</th><th>Best BACK</th><th>Odds floor</th><th>Odds ceiling</th>
   <th>Offer odds</th><th>Accept $</th><th>Stake cap $</th><th>Liability cap $</th><th></th>
  </tr></thead><tbody>
  {market.runners.map(r=>{const o=offers.find(x=>x.selection_id===r.selection_id);
   if(!o)return null;return <tr key={r.selection_id} className={!o.enabled?'eb-off':''}>
    <td><strong>{r.name}</strong><small>#{r.selection_id} {r.status!=='ACTIVE'?' · '+r.status:''}</small></td>
    <td><input aria-label={'Permit offer '+r.name} type="checkbox" checked={o.enabled} onChange={e=>update(r.selection_id,'enabled',e.target.checked)} /></td>
    <td>{r.best_back||'—'}</td>
    {(['min_odds','max_odds','target_odds','accepted_stake','max_accepted_stake','max_runner_liability'] as (keyof Offer)[]).map(f=>
     <td key={f}><input aria-label={r.name+' '+f} type="number" step=".01" min="0" disabled={!o.enabled}
      value={String(o[f])} onChange={e=>update(r.selection_id,f,Number(e.target.value)||0)}/></td>)}
    <td><button disabled={!o.enabled||r.status!=='ACTIVE'||!validTick(o.target_odds)} onClick={()=>approve(o)}>Add test offer</button></td>
   </tr>})}
  </tbody></table></div>
  <div className="eb-summary">
   <div><span>Order intents (simulation)</span><strong>{orders.length}</strong></div>
   <div><span>Matched / open (hypothetical)</span><strong>{M(snapshot?.matched_backer_stake||0)} / {M(snapshot?.open_backer_stake||0)}</strong></div>
   <div><span>Sum of open single-runner liabilities</span><strong>{M(snapshot?.open_single_runner_liability||0)}</strong></div>
   <div><span>Worst race outcome</span><strong className={(snapshot?.worst_case_net||0)<0?'eb-negative':'eb-positive'}>{snapshot?M(snapshot.worst_case_net):'—'}</strong></div>
  </div>
  {message&&<p role="status" className="eb-message">{message}</p>}
  {orders.length>0&&<section className="eb-outcomes eb-lifecycle">
   <h3>Order lifecycle rehearsal — no live exchange traffic</h3>
   <p>One-way simulated exchange events. A cancellation REQUEST does not free liability; exposure is released only after simulated exchange confirmation. Any matched part remains at risk until the race settles.</p>
   <div className="eb-scroll"><table className="eb-table"><thead><tr>
    <th>Runner / limit</th><th>Status</th><th>Matched</th><th>Remaining</th><th>Reserved loss</th><th>Simulate exchange event</th>
   </tr></thead><tbody>{orders.map((o:any)=>{
    const e=L.exposure(o);const r=market.runners.find(x=>x.selection_id===o.selection_id);
    const actionable=['OPEN','PART_MATCHED','CANCEL_REQUESTED','UNKNOWN'].includes(o.status);
    return <tr key={o.intent_id}>
     <td><strong>{r?.name||o.selection_id}</strong><small>LAY @ {o.limit_odds} · {o.events.length} signed-off simulation events</small></td>
     <td><strong>{o.status}</strong>{o.uncertainty&&<small>{o.uncertainty}</small>}</td>
     <td>{M(o.matched_cents/100)}</td><td>{M(e.remaining_stake)}</td>
     <td>{M(e.reserved_worst_liability)}</td>
     <td className="eb-event-actions">
      {actionable&&e.remaining_stake>0&&<button type="button" onClick={()=>simulatedFill(o,false)}>50% fill</button>}
      {actionable&&e.remaining_stake>0&&<button type="button" onClick={()=>simulatedFill(o,true)}>Full fill</button>}
      {['OPEN','PART_MATCHED','UNKNOWN'].includes(o.status)&&<button type="button" onClick={()=>advanceOrder(o,'REQUEST_CANCEL')}>Request cancel</button>}
      {o.status==='CANCEL_REQUESTED'&&<button type="button" onClick={()=>advanceOrder(o,'EXCHANGE_CANCELLED',{exchange_confirmed:true,confirmed_remaining_stake:0})}>Confirm cancel</button>}
      {o.status==='CANCEL_REQUESTED'&&<button type="button" onClick={()=>advanceOrder(o,'CANCEL_UNKNOWN')}>Unknown response</button>}
      {['OPEN','PART_MATCHED'].includes(o.status)&&<button type="button" onClick={()=>advanceOrder(o,'SUBMIT_UNKNOWN')}>Simulate timeout</button>}
      {o.status==='UNKNOWN'&&<button type="button" onClick={()=>advanceOrder(o,'EXCHANGE_ACK',{exchange_status:'SUCCESS',exchange_bet_id:o.exchange_bet_id})}>Resolve accepted</button>}
     </td>
    </tr>;
   })}</tbody></table></div>
  </section>}
  {snapshot&&<div className="eb-outcomes"><h3>If each runner wins — conservative P/L</h3>
   <p>Full exposure to orders on the winning runner, no assumed offsetting new fills on other runners. Matched and unmatched orders must be tracked separately in live execution.</p>
   <div className="eb-outcome-grid">{snapshot.scenarios.map((s:any)=>{const r=market.runners.find(x=>x.selection_id===s.selection_id);
    return <div key={s.selection_id}><small>{r?.name||s.selection_id}</small><strong className={s.worst_net<0?'eb-negative':'eb-positive'}>{M(s.worst_net)}</strong></div>})}</div>
   <p className="eb-caption">One-winner WIN market only. Illustrative 10% commission on positive race P/L; dead-heats, reductions, actual fills and real wallet exposure not yet integrated. No assumed profits or executable orders.</p>
  </div>}
  </>}
 </div>;
}
